import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import {
  applyOptimisticBulkPaste,
  type OptimisticBulkPasteContext,
  rollbackOptimisticBulkPaste,
} from "@/payroll/ai-script/bulk-paste-cache";
import type {
  AcceptedAiSpreadsheetOperationResponse,
  IAiSpreadsheetPasteEdit,
  IManualRowInput,
} from "@/payroll/ai-script/types";
import {
  isRecoverableWorkbookOperationError,
  workbookMutationRequestConfig,
} from "@/payroll/ai-script/workbook-mutation";
import { getAiScriptSpreadsheetPastePath } from "./paths";
import {
  clearSavedManualRowCellDrafts,
  executeWorkbookMutation,
} from "./workbook-refresh";

export interface BulkPasteAiSpreadsheetParams {
  runId: string;
  sheetId: string;
  edits: IAiSpreadsheetPasteEdit[];
  rows: IManualRowInput[];
  baseVersion?: number;
  flushPendingCellEdits?: boolean;
}

export const postAiScriptSpreadsheetPaste = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, edits, rows, baseVersion }: BulkPasteAiSpreadsheetParams,
  idempotencyKey: string = crypto.randomUUID(),
) =>
  api
    .post(
      getAiScriptSpreadsheetPastePath(businessId, runId),
      {
        sheet_id: sheetId,
        edits,
        rows,
        base_version: baseVersion,
      },
      workbookMutationRequestConfig(idempotencyKey),
    )
    .then((res) => res.data as AcceptedAiSpreadsheetOperationResponse);

export const getBulkPasteAiSpreadsheetMutationOptions = ({
  api,
  queryClient,
  businessId,
}: {
  api: AxiosInstance;
  queryClient: QueryClient;
  businessId: string | null;
}) => ({
  mutationFn: (params: BulkPasteAiSpreadsheetParams) => {
    if (!businessId) throw new Error("Business is required");

    return executeWorkbookMutation({
      api,
      queryClient,
      businessId,
      runId: params.runId,
      onApplied: () =>
        clearSavedManualRowCellDrafts(
          queryClient,
          businessId,
          params.runId,
          params.sheetId,
          params.edits.map((edit) => edit.cell_id),
        ),
      flushPendingCellEdits: params.flushPendingCellEdits,
      submit: (idempotencyKey) =>
        postAiScriptSpreadsheetPaste(api, businessId, params, idempotencyKey),
    });
  },
  onMutate: ({ runId, sheetId, edits, rows }: BulkPasteAiSpreadsheetParams) =>
    applyOptimisticBulkPaste({
      queryClient,
      businessId,
      runId,
      sheetId,
      edits,
      rows,
    }),
  onError: (
    error: unknown,
    _variables: BulkPasteAiSpreadsheetParams,
    context: OptimisticBulkPasteContext | undefined,
  ) => {
    if (isRecoverableWorkbookOperationError(error)) return;
    if (context) rollbackOptimisticBulkPaste(queryClient, context);
  },
});

export const useBulkPasteAiSpreadsheet = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    getBulkPasteAiSpreadsheetMutationOptions({ api, queryClient, businessId }),
  );
};
